import type { TransactionalEmailFieldDef } from './constants'
import {
  parseBonusEligibilityRows,
  parseStringArrayField,
  validateBonusEligibilityRows,
} from './array-field-format'
import { getQaRecommendedAdminFieldKeys } from './qa-field-fixtures'
import { LOGIN_CODE_EMAIL_MODE } from './trigger-field-catalog'

function labelForKey (fieldDefs: TransactionalEmailFieldDef[], key: string): string {
  const def = fieldDefs.find((d) => d.key === key)
  return def?.label || key
}

function defForKey (
  fieldDefs: TransactionalEmailFieldDef[],
  key: string,
): TransactionalEmailFieldDef | undefined {
  return fieldDefs.find((d) => d.key === key)
}

function isBooleanFieldValueValid (raw: string): boolean {
  const normalized = String(raw || '')
    .trim()
    .toLowerCase()
  return normalized === 'true' || normalized === 'false'
}

function isAdminFieldValuePresent (
  def: TransactionalEmailFieldDef | undefined,
  raw: string,
): boolean {
  const inputType = def?.inputType || 'text'
  if (inputType === 'boolean') {
    return isBooleanFieldValueValid(raw)
  }
  if (inputType === 'stringArray' || inputType === 'entitledSkuKeys') {
    return parseStringArrayField(raw).length > 0
  }
  if (inputType === 'bonusEligibilityRows') {
    const rows = parseBonusEligibilityRows(raw)
    return validateBonusEligibilityRows(rows).length === 0
  }
  return String(raw || '').trim().length > 0
}

export function getCustomEventSendValidationErrors (
  templateId: string,
  fieldValues: Record<string, string>,
  fieldDefs: TransactionalEmailFieldDef[],
): string[] {
  const errors: string[] = []
  const missingRequired = fieldDefs
    .filter((def) => def.required && !isAdminFieldValuePresent(def, fieldValues[def.key] || ''))
    .map((def) => def.label)
  if (missingRequired.length > 0) {
    errors.push(`Missing required fields: ${missingRequired.join(', ')}`)
  }

  const qaRecommended = getQaRecommendedAdminFieldKeys(templateId)
  const missingQa = qaRecommended
    .filter((key) => {
      const def = defForKey(fieldDefs, key)
      if (def?.inputType === 'bonusEligibilityRows') {
        const rows = parseBonusEligibilityRows(fieldValues[key] || '')
        if (rows.length > 0) {
          return false
        }
      }
      return !isAdminFieldValuePresent(def, fieldValues[key] || '')
    })
    .map((key) => labelForKey(fieldDefs, key))
  if (missingQa.length > 0) {
    errors.push(`Missing recommended fields for a complete email: ${missingQa.join(', ')}`)
  }

  qaRecommended.forEach((key) => {
    const def = defForKey(fieldDefs, key)
    if (def?.inputType !== 'bonusEligibilityRows') {
      return
    }
    const rowErrors = validateBonusEligibilityRows(
      parseBonusEligibilityRows(fieldValues[key] || ''),
    )
    rowErrors.forEach((msg) => errors.push(msg))
  })

  if (templateId === 'LOGIN_CODE') {
    const mode = String(fieldValues.email_mode || '').trim()
    if (
      mode === LOGIN_CODE_EMAIL_MODE.CODE_AND_LINK &&
      !String(fieldValues.digits_code || '').trim()
    ) {
      errors.push('Digits code is required when email mode is code-and-link')
    }
  }
  return errors
}
